import React, { useEffect, useState } from "react";
import { addToWaitlist, patientHasAppointment } from "../../APIs/waitlistApi";
import { lookupAppointmentByStart } from "../../APIs/appointmentsApi";
import CompletePatientProfileModal from "../../Patient/CompletePatientProfileModal";
import { fetchCurrentUser } from "../../APIs/authApi";
import keycloak from "../../APIs/keycloak";
import { showToast } from "../../APIs/utils/toastManager";

function fmtDateTime(zdtLike, zone) {
  if (!zdtLike) return { date: "", time: "" };
  const iso = String(zdtLike).split("[")[0];
  const d = new Date(iso);
  const date = d.toLocaleDateString([], {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: zone,
  });
  const time = d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: zone,
  });
  return { date, time };
}

export default function WaitlistModal({
  open,
  onClose,
  providerId,
  patientId,
  providerName,
  slotStart,
  appointmentId,
  zone,
}) {
  const [reasonForWaitlist, setReasonForWaitlist] = useState("");
  const [allowProviderContact, setAllowProviderContact] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [showCompleteProfileModal, setShowCompleteProfileModal] =
    useState(false);
  const [alreadyBooked, setAlreadyBooked] = useState(false);
  const [checkingBooking, setCheckingBooking] = useState(false);
  const token = keycloak?.token;

  const { date, time } = fmtDateTime(slotStart, zone || "America/New_York");

  // Reset + check booking on open
  useEffect(() => {
    if (!open) return;
    setReasonForWaitlist("");
    setAllowProviderContact(false);
    setErr("");
    setAlreadyBooked(false);

    if (!patientId || !providerId || !appointmentId) {
      setCheckingBooking(false);
      return;
    }

    setCheckingBooking(true);
    (async () => {
      try {
        const exists = await patientHasAppointment({
          providerId: Number(providerId),
          appointmentId: Number(appointmentId),
          patientId: Number(patientId),
        });
        setAlreadyBooked(!!exists);
      } catch (e) {
        console.error("Failed to check appointment:", e);
        setAlreadyBooked(false);
      } finally {
        setCheckingBooking(false);
      }
    })();
  }, [open, patientId, providerId, appointmentId]);

  // Load current user for account phase gating
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const me = await fetchCurrentUser();
        setCurrentUser(me);
      } catch {
        setCurrentUser(null);
      }
    })();
  }, [open]);

  const handleJoinWaitlistCore = async () => {
    setErr("");
    if (!patientId) {
      setErr("Missing patient ID.");
      return;
    }
    if (!providerId) {
      setErr("Missing provider ID.");
      return;
    }

    try {
      setSubmitting(true);
      let effectiveAppointmentId = appointmentId
        ? Number(appointmentId)
        : undefined;

      if (!effectiveAppointmentId) {
        if (!slotStart) throw new Error("Missing appointment start time.");
        const lookup = await lookupAppointmentByStart(Number(providerId), {
          start: slotStart,
          zone: zone || "America/New_York",
        });
        if (!lookup?.appointmentId)
          throw new Error("Could not find appointment by start time.");
        effectiveAppointmentId = Number(lookup.appointmentId);
      }

      await addToWaitlist(Number(patientId), {
        appointmentId: effectiveAppointmentId,
        providerId: Number(providerId),
        reasonForWaitlist: reasonForWaitlist?.trim() || undefined,
        allowProviderContact,
      });

      showToast("You've been added to the waitlist!", "success");
      onClose();
    } catch (e) {
      setErr(
        e?.response?.data?.message ||
          e?.response?.data ||
          e?.message ||
          "Failed to join waitlist",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      setErr("Unable to determine your account status. Please sign in again.");
      return;
    }
    if (currentUser.accountPhase === "BASIC_ACCOUNT") {
      setShowCompleteProfileModal(true);
      return;
    }
    await handleJoinWaitlistCore();
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">Join Waitlist</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            >
              ✕
            </button>
          </div>

          <div className="px-6 py-5 space-y-4">
            {checkingBooking ? (
              <p className="text-gray-500 text-sm">
                Checking appointment status...
              </p>
            ) : alreadyBooked ? (
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                <p className="text-blue-800 font-medium text-sm">
                  You already have this appointment booked.
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  You cannot join the waitlist for an appointment you've already
                  booked.
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  This time is currently booked. Join the waitlist and we'll
                  notify you if it opens up.
                </p>

                {/* Appointment summary */}
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-2">
                  {providerName && (
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold tracking-wide">
                        Provider
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {providerName}
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold tracking-wide">
                        Date
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {date || "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold tracking-wide">
                        Time
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {time || "—"}
                      </p>
                    </div>
                  </div>
                </div>

                {err && (
                  <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 text-sm">
                    {err}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reason (optional)
                    </label>
                    <textarea
                      value={reasonForWaitlist}
                      onChange={(e) => setReasonForWaitlist(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none"
                      placeholder="e.g., Prefer an earlier slot, urgent follow-up, etc."
                      rows={3}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      id="wl-allowContact"
                      type="checkbox"
                      checked={allowProviderContact}
                      onChange={(e) =>
                        setAllowProviderContact(e.target.checked)
                      }
                      className="h-4 w-4 accent-teal-600"
                    />
                    <label
                      htmlFor="wl-allowContact"
                      className="text-sm text-gray-700"
                    >
                      Allow the provider to contact me about this opening.
                    </label>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={onClose}
                      className="text-sm font-medium text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-5 py-2 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? "Joining…" : "Join Waitlist"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      <CompletePatientProfileModal
        isOpen={showCompleteProfileModal}
        onClose={() => setShowCompleteProfileModal(false)}
        token={token}
        currentUser={currentUser}
        onCompleted={async () => {
          setCurrentUser((prev) =>
            prev ? { ...prev, accountPhase: "COMPLETED_PROFILE" } : prev,
          );
          setShowCompleteProfileModal(false);
          await new Promise((resolve) => setTimeout(resolve, 500));
          try {
            await keycloak.updateToken(30);
          } catch {}
          await handleJoinWaitlistCore();
        }}
      />
    </>
  );
}
